import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { pushForm } from '../utils/formStack';

// ── Toast ───────────────────────────────────────────────────────────────────
const ToastCtx = createContext(null);
export const useToast = () => useContext(ToastCtx);
export function ToastProvider({ children }) {
  const [msg, setMsg] = useState(null);
  const show = useCallback((m) => {
    setMsg(m);
    setTimeout(() => setMsg(null), 2600);
  }, []);
  return (
    <ToastCtx.Provider value={show}>
      {children}
      {msg && <div className="toast">{msg}</div>}
    </ToastCtx.Provider>
  );
}

// ── Modal ───────────────────────────────────────────────────────────────────
// Registers with the global formStack so the Android back button can clear-then-
// close it. Pass `isDirty` (→ back clears the form) and `onClear` (how to reset);
// without them, back simply closes. `onClose` is always the close action.
// `wide` → ~1040px (multi-column forms); `dense` → tighter fields so the form fits one screen.
export function Modal({ title, onClose, children, footer, isDirty, onClear, className = '', fullScreen, wide, dense }) {
  // Keep the latest callbacks in a ref so the once-registered handler always
  // sees current values without re-registering each render.
  const cb = useRef({});
  cb.current = { onClose, isDirty, onClear };
  useEffect(() => {
    const unregister = pushForm({
      isDirty: () => (cb.current.isDirty ? !!cb.current.isDirty() : false),
      clear: () => { cb.current.onClear && cb.current.onClear(); },
      close: () => { cb.current.onClose && cb.current.onClose(); },
    });
    return unregister;
  }, []);
  return (
    <div className={`modal-back${fullScreen ? ' full' : ''}`} onClick={onClose}>
      <div className={`modal${fullScreen ? ' full' : ''}${wide ? ' wide' : ''}${dense ? ' dense' : ''}${className ? ` ${className}` : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>{title}</h3>
          <button className="x" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

// ── Field ───────────────────────────────────────────────────────────────────
export function Field({ label, type = 'text', value, onChange, options, required, placeholder, rows }) {
  return (
    <label className="field">
      {label}{required && ' *'}
      {type === 'select' ? (
        <select value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
          <option value="">- select -</option>
          {(options || []).map(o => (
            <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
          ))}
        </select>
      ) : type === 'textarea' ? (
        <textarea rows={rows || 3} value={value ?? ''} placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input type={type} value={value ?? ''} placeholder={placeholder}
          inputMode={type === 'number' ? 'decimal' : undefined}
          onChange={(e) => onChange(e.target.value)} />
      )}
    </label>
  );
}

export function Spinner({ label = 'Loading...' }) {
  return <div className="empty">{label}</div>;
}
